/**
 * Who may share a dev server (owner decision, Run & Share Phase 2):
 *
 *   allowed      the plan has `tunnels_limit > 0` (Pro, Team)
 *   upgrade      the plan exposes `tunnels_limit` as 0 (Free, Basic) — Share
 *                opens the plan upgrade dialog
 *   unavailable  the server answered `POST /tunnels` with 403 feature_disabled
 *                (the server-side switch is off) — "Sharing isn't available yet"
 *                — or `GET /tunnels/usage` says `enabled: false`, before any POST
 *   suspended    `GET /tunnels/usage` says `share_suspended` (an admin took
 *                Share away from this account)
 *   hidden       no source exposes `tunnels_limit`: the server does not offer
 *                sharing (yet), so no Share button
 *
 * Source of truth: `GET /tunnels/usage` (`enabled` + `tunnels_limit`; it
 * includes the server switch). Only when that call FAILS: `/auth/usage`
 * (`user._usage.limits.tunnels_limit`), then the plans catalog (`GET /plans`).
 * The server re-checks on every create; this only decides what the button does.
 */

/** The user's `tunnels_limit`, or null when no source names one. */
export function tunnelsLimit(user, plans) {
  const fromUsage = user?._usage?.limits?.tunnels_limit;
  if (typeof fromUsage === 'number') return fromUsage;
  const planId = String(user?.plan || 'free').toLowerCase();
  const plan = Array.isArray(plans) ? plans.find((p) => String(p?.id || '').toLowerCase() === planId) : null;
  const fromPlans = plan?.limits?.tunnels_limit;
  return typeof fromPlans === 'number' ? fromPlans : null;
}

export function shareAccess({ limit, disabled, usage }) {
  if (disabled) return 'unavailable';
  if (usage) {
    if (usage.enabled === false) return 'unavailable';
    if (usage.suspended) return 'suspended';
    if (typeof usage.tunnelsLimit === 'number') return usage.tunnelsLimit > 0 ? 'allowed' : 'upgrade';
  }
  if (typeof limit !== 'number') return 'hidden';
  return limit > 0 ? 'allowed' : 'upgrade';
}

/** "1.2 MB" style sizes for the bandwidth meter. */
export function formatBytes(n) {
  const v = Math.max(0, Number(n) || 0);
  if (v < 1024) return `${v} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let x = v / 1024;
  let i = 0;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i += 1;
  }
  return `${x < 10 ? x.toFixed(1) : Math.round(x)} ${units[i]}`;
}

/** "1:05:09" / "4:09" until `expiresAt` (ms); "0:00" once passed. */
export function formatCountdown(expiresAt, now = Date.now()) {
  const left = Math.max(0, Math.floor((expiresAt - now) / 1000));
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const pad = (x) => String(x).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
